import type { WebhookEvent } from '@emailforge/schemas';
import { Button, Chip, Input } from '@emailforge/ui';
import { useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  useCreateWebhookMutation,
  useDeleteWebhookMutation,
  useListDeliveriesQuery,
  useListWebhooksQuery,
  useRotateSecretMutation,
  useTestWebhookMutation,
} from '@/features/webhooks/webhooksApi';
import { t } from '@/lib/i18n';

const ALL_EVENTS: { event: WebhookEvent; label: string }[] = [
  { event: 'send.queued', label: 'Send queued' },
  { event: 'send.sent', label: 'Send sent' },
  { event: 'send.delivered', label: 'Send delivered' },
  { event: 'send.opened', label: 'Send opened' },
  { event: 'send.clicked', label: 'Send clicked' },
  { event: 'send.bounced', label: 'Send bounced' },
  { event: 'send.complained', label: 'Send complained' },
  { event: 'send.deferred', label: 'Send deferred' },
  { event: 'send.failed', label: 'Send failed' },
  { event: 'template.published', label: 'Template published' },
  { event: 'experiment.winner_declared', label: 'Experiment winner declared' },
];

export function WebhooksScreen() {
  const { data: endpoints, isLoading } = useListWebhooksQuery();
  const [create, { isLoading: isCreating }] = useCreateWebhookMutation();
  const [rotate] = useRotateSecretMutation();
  const [test] = useTestWebhookMutation();
  const [remove] = useDeleteWebhookMutation();

  const [showForm, setShowForm] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>(['send.delivered', 'send.bounced']);

  const submit = async () => {
    const res = await create({ url, description: description || undefined, events }).unwrap();
    setRevealedSecret(res.secret);
    setShowForm(false);
    setUrl('');
    setDescription('');
  };

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.webhooks')]}
        right={
          !showForm ? (
            <Button variant="flame" size="sm" onClick={() => setShowForm(true)}>
              <Icon.plus size={13} /> New endpoint
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          )
        }
      />
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: 24 }}>
          {revealedSecret && (
            <div
              className="mf-card"
              style={{
                padding: 18,
                marginBottom: 16,
                background: 'linear-gradient(135deg, var(--flame-soft), var(--paper))',
                borderColor: 'var(--flame-line)',
              }}
            >
              <div
                className="mono"
                style={{ fontSize: 10.5, color: 'var(--flame-2)', textTransform: 'uppercase', letterSpacing: 0.08 }}
              >
                Signing secret — copy now, won't be shown again
              </div>
              <div
                className="mono"
                style={{
                  padding: 10,
                  background: 'var(--ink)',
                  color: 'var(--paper)',
                  borderRadius: 'var(--r-2)',
                  fontSize: 12.5,
                  wordBreak: 'break-all',
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span style={{ flex: 1 }}>{revealedSecret}</span>
                <Button size="sm" style={{ background: 'var(--paper-3)', color: 'var(--ink)' }}
                  onClick={() => navigator.clipboard.writeText(revealedSecret)}
                >
                  <Icon.copy size={12} /> Copy
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setRevealedSecret(null)} style={{ marginTop: 10 }}>
                Close
              </Button>
            </div>
          )}

          {showForm ? (
            <div className="mf-card" style={{ padding: 24, maxWidth: 640 }}>
              <h2 className="display" style={{ fontSize: 22, margin: '0 0 16px' }}>
                New webhook endpoint
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="URL">
                  <Input mono
                    
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://your-app.com/hooks/emailforge" />
                </Field>
                <Field label="Description (optional)">
                  <Input
                    
                    value={description}
                    onChange={(e) => setDescription(e.target.value)} />
                </Field>
                <Field label="Events">
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 6,
                    }}
                  >
                    {ALL_EVENTS.map((e) => (
                      <label
                        key={e.event}
                        style={{
                          display: 'flex',
                          gap: 6,
                          padding: 8,
                          border: `1px solid ${events.includes(e.event) ? 'var(--flame)' : 'var(--line)'}`,
                          borderRadius: 'var(--r-2)',
                          cursor: 'pointer',
                          background: events.includes(e.event) ? 'var(--flame-soft)' : 'var(--paper)',
                          fontSize: 12,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={events.includes(e.event)}
                          onChange={(ev) =>
                            setEvents(ev.target.checked ? [...events, e.event] : events.filter((x) => x !== e.event))
                          }
                        />
                        <span className="mono" style={{ fontSize: 10.5 }}>
                          {e.event}
                        </span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Button variant="flame" onClick={submit}
                  disabled={!url || events.length === 0 || isCreating}>
                  {isCreating ? 'Creating…' : 'Create endpoint'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="display" style={{ fontSize: 28, margin: '0 0 8px' }}>
                Webhooks
              </h1>
              <p style={{ color: 'var(--ink-2)', fontSize: 13.5, marginBottom: 20 }}>
                Subscribe to send / experiment events. Payloads signed with HMAC-SHA256 (
                <code className="mono" style={{ fontSize: 11 }}>
                  X-EmailForge-Signature
                </code>
                ). 5 retries with exponential backoff.
              </p>

              {isLoading && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Loading…
                </div>
              )}

              {!isLoading && (endpoints ?? []).length === 0 && (
                <div className="mf-card" style={{ padding: 32, textAlign: 'center' }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.08,
                      color: 'var(--ink-3)',
                    }}
                  >
                    No endpoints yet
                  </div>
                  <Button variant="flame" size="sm" onClick={() => setShowForm(true)} style={{ marginTop: 14 }}>
                    <Icon.plus size={13} /> New endpoint
                  </Button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {endpoints?.map((e) => (
                  <div key={e.id} className="mf-card" style={{ padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div className="mono" style={{ fontSize: 12.5, fontWeight: 550, wordBreak: 'break-all' }}>
                          {e.url}
                        </div>
                        {e.description && (
                          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{e.description}</div>
                        )}
                      </div>
                      <Chip variant={e.isActive ? 'live' : 'draft'}>
                        {e.isActive ? 'active' : 'paused'}
                      </Chip>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {e.events.map((ev) => (
                        <Chip dot={false} className="mono" key={ev}  style={{ fontSize: 10 }}>
                          {ev}
                        </Chip>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => setSelected(selected === e.id ? null : e.id)}>
                        {selected === e.id ? 'Hide deliveries' : 'View deliveries'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={async () => {
                          const r = await rotate(e.id).unwrap();
                          setRevealedSecret(r.secret);
                        }}
                      >
                        Rotate secret
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => test(e.id)}>
                        Test
                      </Button>
                      <Button variant="ghost" size="sm" onClick={async () => {
                          if (window.confirm(`Delete ${e.url}?`)) await remove(e.id).unwrap();
                        }}
                      >
                        <Icon.trash size={12} />
                      </Button>
                    </div>
                    {selected === e.id && <DeliveriesTable endpointId={e.id} />}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function DeliveriesTable({ endpointId }: { endpointId: string }) {
  const { data } = useListDeliveriesQuery(endpointId, { pollingInterval: 5000 });
  return (
    <div
      style={{
        marginTop: 14,
        border: '1px solid var(--line-soft)',
        borderRadius: 'var(--r-2)',
        overflow: 'hidden',
      }}
    >
      <table className="mf-table">
        <thead>
          <tr>
            <th>Event</th>
            <th>Status</th>
            <th>Attempts</th>
            <th>Delivered</th>
            <th>Error</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((d) => (
            <tr key={d.id}>
              <td className="mono" style={{ fontSize: 11 }}>
                {d.eventType}
              </td>
              <td>
                {d.statusCode ? (
                  <Chip variant={d.statusCode >= 200 && d.statusCode < 300 ? 'live' : 'err'}>
                    {d.statusCode}
                  </Chip>
                ) : (
                  <Chip variant="draft">pending</Chip>
                )}
              </td>
              <td className="mono" style={{ fontSize: 11 }}>
                {d.attempts}
              </td>
              <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                {d.deliveredAt ? new Date(d.deliveredAt).toLocaleTimeString() : '—'}
              </td>
              <td className="mono" style={{ fontSize: 10.5, color: 'var(--err)' }}>
                {d.errorMessage ?? ''}
              </td>
              <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                {new Date(d.createdAt).toLocaleTimeString()}
              </td>
            </tr>
          ))}
          {(data ?? []).length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--ink-3)' }}>
                No deliveries yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
